import type { DatasourceMap } from '../datasource.types'

// CrowdStrike LogScale datasource mappings
// Maps category + platform -> event_simpleName values used in the LogScale prelude
//
// Extracted into a standalone data file so datasource.registry can import it
// without pulling in the full tool class (which depends on datasource.utils ->
// datasource.resolve -> datasource.registry, creating a circular dependency).
export const logscaleMap: DatasourceMap = {
  'CrowdStrike LogScale': {
    // Cloud provider categories — routing via #event.module / #event.dataset.
    // No #event_simpleName or event_platform is emitted for these.
    azure: {
      cloud: {
        repoSubtype: 'azure',
        eventProvider: 'azure.activity',
      },
      // Could project extra.platformClauseList to use event.action to point to -> azure.auditlogs, azure.signinlogs, etc. per more specific categories
    },
    aws: {
      cloud: {
        repoSubtype: 'aws',
        eventProvider: 'aws.cloudtrail',
      },
    },
    gcp: {
      cloud: {
        repoSubtype: 'gcp',
        eventProvider: 'gcp.audit',
      },
    },

    // Cross-platform categories: same event_simpleName on Windows, Linux, and Mac.
    // The event_platform filter (e.g. event_platform=Win) is still emitted by the
    // renderer when the directive specifies a platform — no duplication needed here
    // Only create a new configuration for other platforms IF THE FIELD NAMES diverge from standard

    any: {
      process_creation: {
        events: ['ProcessRollup2', 'SyntheticProcessRollup2'],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
      },
      file_event: {
        // Updated event search to cover all potential events, instead of just executable events -- bkd 03292026
        events: [
          '*Written*',
          'CriticalFileAccessed',
          'FileCreateInfo',
          'FileOpenInfo',
          'FileRenameInfo',
          'RansomwareOpenFile'
        ], // More inclusive catch-all patterns since CrowdStrike has been adding new file events with varying names
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        // MD5HashData is only present on module-load / ProcessRollup2 events,
        // not on file-write events. Route md5 IOCs to image_load / process_creation instead.
        unsupportedFields: ['md5'],
      },
      // Only file_event and process_creation carry a full-path acting-process
      // field on LogScale (ContextImageFileName / ImageFileName). Every other
      // category's process_* overrides collapse to ContextBaseFileName (or
      // equivalent basename-only) — so a path-valued IOC routed to
      // process_path would search a basename field and never match. Drop
      // process_path in those categories.
      registry_event: {
        events: [
          'RegGenericValueUpdate',
          'RegSystemConfigValueUpdate',
          'RegistryOperationDetectInfo',
          'SuspiciousRegAsepUpdate',
          'AsepValueUpdate',
        ],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
      },
      dns: {
        events: ['DnsRequest', 'SuspiciousDnsRequest'],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        unsupportedFields: ['process_path'],
      },
      network_connection: {
        events: ['NetworkConnectIP4'],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        // NetworkConnectIP4 only carries IP fields — DomainName lives on DNS
        // events and HttpUrl only on HttpRequestDetect.
        unsupportedFields: ['domain', 'url', 'process_path'],
      },
      image_load: {
        events: ['ClassifiedModuleLoad'],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        unsupportedFields: ['process_path'],
      },
      system: {
        events: [
          'CreateService',
          'ModifyServiceBinary',
          'ServiceStarted',
          'ServicesStatusInfo',
        ],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        unsupportedFields: ['process_path'],
      },
      security: {
        events: [
          'ScheduledTaskModified',
          'ScheduledTaskRegistered',
          'ScheduledTaskDeleted',
          'ScriptControlScanInfo',
          'ScriptControlDetectInfo',
        ],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        unsupportedFields: ['process_path'],
      },
      authentication_events: {
        events: [
          'UserLogon',
          'UserLogonFailed2',
          'UserLogonFailed',
          'SsoUserLogonFailure',
          'ActiveDirectoryInteractiveDomainLogon',
          'ActiveDirectoryAuthenticationFailure',
          'ActiveDirectoryAuthentication',
          'SSHClientAuthenticated',
        ],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        unsupportedFields: ['process_path'],
      },
      // Missing coverage -- bkd 03292026
      pipe_created: {
        events: [
          'NamedPipeCreatedInfo',
          'NamedPipeDetectInfo',
        ],
        repo: 'base_sensor',
        extra: { platformClause: 'event_platform' },
        unsupportedFields: ['target_path'],
      },
      // Unsupported categories as defined in allowable types in zod/Hunter
      //webserver:{},
      //proxy:{},
      //Rdp_event:{},  // No distinct RDP event_simpleName; covered by authentication_events
      //firewall:{},
      //application:{},
    },
  },
}
